import ClearIcon from '@mui/icons-material/Clear';
import { Chip, SelectChangeEvent } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertMessageType } from 'basics/enums/maya.enums';
import { SelectOption } from 'basics/options/options.types';
import AlertBanner from 'components/AlertBanner/AlertBanner';
import { FormLayoutContainer } from 'components/FormLayout';
import GridList from 'components/GridList/GridList';
import ClearAllIcon from 'components/Icons/ClearAllIcon';
import MayaLayout from 'components/MayaLayout';
import {
  filtersFormStyle,
  filtersHeaderStyle,
  filtersResultsStyle,
  filtersResultsValueStyle,
  filtersSectionStyle,
  filtersTitleStyle,
  resetButtonStyle,
  selectedTagsStyle,
  sortSelectStyle,
  sortSelectWrapperStyle,
} from 'routes/AuthenticatedApp/CepList/CepList.style';
import useCepTemplateList from 'routes/AuthenticatedApp/CepTemplateList/CepTemplateList.hook';
import Box from 'yoda-ui/components/Box';
import ConfirmationDialog from 'yoda-ui/components/Dialogs/ConfirmationDialog';
import Select from 'yoda-ui/components/Form/Select';
import MultiSelectYoda from 'yoda-ui/components/Form/SelectYoda/MultiSelectYoda';
import { TagStyle } from 'yoda-ui/components/Form/SelectYoda/MultiSelectYoda/MultiSelectYoda.styles';
import TextYoda from 'yoda-ui/components/Form/TextYoda';
import Loader from 'yoda-ui/components/Loader';
import MenuItem from 'yoda-ui/components/MenuItem';
import { YodaFormProvider } from 'yoda-ui/yodaForm';
import { YodaColors, YodaFontSize } from 'yoda-ui/yodaTheme';

const CepTemplateList: FC = () => {
  const { t } = useTranslation();
  const flags = useFlags();

  const isCepFilterByTAreaProductFunctionEnabled = flags.mayaAkma419EnableSiCiCepListAdditionalFilters;

  const {
    cepListColumnConfig,
    cepLoading,
    cepTitleFieldConfig,
    debouncedHandleChangeCepSearch,
    deleteCepCallback,
    cepTherapeuticAreaFieldConfig,
    handleChangeCepTherapeuticArea,
    cepProductFieldConfig,
    handleChangeCepProduct,
    cepBiogenFunctionFieldConfig,
    handleChangeCepBiogenFunction,
    getFilteredRows,
    menuCustomerEngagementPlanningLabel,
    newElementButton,
    onPageChange,
    onPageSizeChange,
    open,
    removeCepLoading,
    rowCountState,
    rowId,
    setOpen,
    providerFields,
    selectedFilters,
    handleRemoveChip,
    handleReset,
    hasSelectedFilters,
    cepSortFieldConfig,
    handleSortChange,
    hasActiveFilters,
    cepSortFilter,
  } = useCepTemplateList();

  return (
    <MayaLayout>
      <AlertBanner type={ AlertMessageType.VeevaFatalErrorAlert } />
      <GridList
        loading={ cepLoading }
        getRowId={ (row) => row._id }
        title={ menuCustomerEngagementPlanningLabel }
        columnConfig={ cepListColumnConfig }
        rows={ getFilteredRows() }
        onPageChange={ onPageChange }
        onPageSizeChange={ onPageSizeChange }
        rowCountState={ rowCountState }
        newElementButton={ newElementButton }
        customFilters={
          (
            <YodaFormProvider { ...providerFields }>
              <Box sx={ filtersSectionStyle }>
                <Box sx={ filtersHeaderStyle }>
                  <Box sx={ filtersTitleStyle }>{ t('maya_ci_filter_title') }</Box>
                  {
                    !cepLoading && (
                      <Box sx={ filtersResultsStyle }>
                        { t('maya_ci_filter_results_title') }<Box
                          component="span"
                          sx={ filtersResultsValueStyle }>
                          { t('maya_ci_filter_results_value', { shownItems: getFilteredRows().length, allItems: rowCountState }) }
                        </Box>
                      </Box>
                    )
                  }
                </Box>

                <FormLayoutContainer sx={ filtersFormStyle }>
                  <Box>
                    <TextYoda
                      onChange={ debouncedHandleChangeCepSearch }
                      { ...cepTitleFieldConfig } />
                  </Box>
                  {
                    isCepFilterByTAreaProductFunctionEnabled && <Box>
                      <MultiSelectYoda
                        onChange={ handleChangeCepTherapeuticArea }
                        { ...cepTherapeuticAreaFieldConfig } />
                    </Box>
                  }
                  {
                    isCepFilterByTAreaProductFunctionEnabled && <Box>
                      <MultiSelectYoda
                        onChange={ handleChangeCepProduct }
                        { ...cepProductFieldConfig } />
                    </Box>
                  }
                  {
                    isCepFilterByTAreaProductFunctionEnabled && <Box>
                      <MultiSelectYoda
                        onChange={ handleChangeCepBiogenFunction }
                        { ...cepBiogenFunctionFieldConfig } />
                    </Box>
                  }
                </FormLayoutContainer>

                {
                  hasActiveFilters
                  && <Box sx={ selectedTagsStyle }>
                    {
                      selectedFilters.therapeuticArea.map((option) => (
                        <Chip
                          key={ option.id }
                          label={ option.label }
                          sx={ TagStyle }
                          onDelete={ () => handleRemoveChip('therapeuticArea', option.id) }
                          deleteIcon={ <ClearIcon /> }
                        />
                      ))
                    }
                    {
                      selectedFilters.product.map((option) => (
                        <Chip
                          key={ option.id }
                          label={ option.label }
                          sx={ TagStyle }
                          onDelete={ () => handleRemoveChip('product', option.id) }
                          deleteIcon={ <ClearIcon /> }
                        />
                      ))
                    }
                    {
                      selectedFilters.cepFunction.map((option) => (
                        <Chip
                          key={ option.id }
                          label={ option.label }
                          sx={ TagStyle }
                          onDelete={ () => handleRemoveChip('cepFunction', option.id) }
                          deleteIcon={ <ClearIcon /> }
                        />
                      ))
                    }
                    {
                      hasSelectedFilters && (
                        <Box onClick={ handleReset } sx={ resetButtonStyle }>
                          <ClearAllIcon />
                          { t('actions_reset_label') }
                        </Box>
                      )
                    }
                  </Box>
                }
              </Box>
              <Box sx={ sortSelectWrapperStyle }>
                <Select
                  value={ cepSortFilter }
                  onChange={
                    (event: SelectChangeEvent<unknown>, _child: ReactNode) => {
                      const selectedOption = cepSortFieldConfig.options.find((opt) => opt.id === event.target.value);
                      if (selectedOption) {
                        handleSortChange(selectedOption);
                      }
                    }
                  }
                  sx={ sortSelectStyle }
                >
                  {
                    cepSortFieldConfig.options.map(
                      (option: SelectOption) => {
                        return <MenuItem key={ option.id }
                          value={ option.id } sx={ { fontSize: YodaFontSize.small } }>{ option.label }</MenuItem>;
                      },
                    )
                  }
                </Select>
              </Box>
            </YodaFormProvider>
          )
        }
        disableColumnMenu={ true }
      />
      {
        open && (
          <ConfirmationDialog
            dialogTitle={ t('maya_cep_modal_remove') }
            onConfirm={
              () => {
                deleteCepCallback(rowId);
              }
            }
            onConfirmLabel={ removeCepLoading ? <Loader center size={ 1 } color={ YodaColors.inherit } /> : t('actions_confirm_label') }
            onDismiss={ () => setOpen(false) }
            onDismissLabel={ t('actions_cancel_label') }
          >
          </ConfirmationDialog>
        )
      }
    </MayaLayout>
  );
};

export default CepTemplateList;
